import { clone } from 'es-toolkit';
import { CountryCode } from 'libphonenumber-js';
import { action, computed, makeObservable, observable } from 'mobx';
import { arrayWithNewItem, arrayWithoutExistingItem } from '../../common/array';
import {
  Award,
  blankContact,
  blankCountry,
  blankCustomerReference,
  blankNomination,
  blankNominationWebsite,
  Country,
  createOrganizationDraft,
  DocumentMetadata,
  Nomination,
  Organization,
  OrganizationType,
  Owner,
  UserInfo,
} from '../../models';
import {
  AdditionalContactValidation,
  additionalContactValidationSchema,
  CustomerReferenceValidation,
  customerReferenceValidationSchema,
  NominationWebsiteValidation,
  nominationWebsiteValidationSchema,
} from '../../models/schemas/nomination-validation.schema';
import {
  NominationSubmissionValidation,
  nominationSubmissionValidationSchema,
  NominationWizardCompanyInformationValidation,
  nominationWizardCompanyInformationValidationSchema,
  NominationWizardJointNomineeValidation,
  nominationWizardJointNomineeValidationSchema,
  NominationWizardValidationInput,
  YourInformationValidation,
  yourInformationValidationSchema,
} from '../../models/schemas/nomination-wizard-validation.schema';
import { createValidationState, ValidationState } from '../../shared/validation';
import { PhoneNumberField, PhoneNumberFieldId } from '../shared-components/phone-number-input';
import type { NominationStepId } from './nomination-stepper';

export type NominationValidatedSteps = Partial<Record<NominationStepId, boolean>>;

export type NominationWizardLoadingType = 'load' | 'save';

export class NominationWizardPageUiStore {
  @observable nomination: Nomination = blankNomination();

  @observable validatedSteps: NominationValidatedSteps = {};

  @observable executiveSummaryDocument?: DocumentMetadata;

  @observable writtenNominationContent?: string;

  @observable deletedContactIds: number[] = [];

  @observable deletedJointNomineeIds: number[] = [];

  @observable deletedCustomerReferenceIds: number[] = [];

  @observable deletedNominationWebsitesIds: number[] = [];

  @observable companyInfoDescription: string = '';

  @observable nominationSubmissionDescription: string = '';

  furthestVisitedStepId: NominationStepId = 'your-information';

  @observable selectedAward?: Award;

  @observable editingAssociatedCountries: Country[] = [];

  @observable countryCodeMapById: Map<PhoneNumberFieldId, CountryCode> = new Map();

  @observable initialStepId: NominationStepId = 'your-information';

  @observable contactRowKeys: string[] = this.nomination.contacts.map(() => crypto.randomUUID());

  @observable jointNomineeRowKeys: string[] = [];

  @observable isCeoEmailIncluded: boolean = false;

  @observable jointNomineeCeoEmailIncludedMap: Map<string, boolean> = new Map();

  @observable customerReferenceRowKeys: string[] = [];

  @observable nominationWebsiteRowKeys: string[] = [];

  @observable expandedContactKeys: string[] = [];

  @observable expandedJointNomineeKeys: string[] = [];

  @observable expandedCustomerReferenceKeys: string[] = [];

  @observable isConfirmPartnerIdModalOpen = false;

  @observable loadingType?: NominationWizardLoadingType = 'load';

  constructor() {
    makeObservable(this);
  }

  @computed
  get isSubmitted(): boolean {
    return this.nomination.status === 'Submitted';
  }

  @computed
  get nominationOrganization(): Organization {
    return this.nomination.organization ?? createOrganizationDraft(OrganizationType.Main);
  }

  @computed
  get hasExecutiveSummaryDocument(): boolean {
    return this.executiveSummaryDocument !== undefined;
  }

  @computed
  get isLoadingOverlayVisible(): boolean {
    return this.loadingType === 'save';
  }

  isBeingValidated(stepId: NominationStepId): boolean {
    return Boolean(this.validatedSteps[stepId]);
  }

  isOrganizationBeingValidated(organizationTypeId: OrganizationType): boolean {
    return this.isBeingValidated(
      organizationTypeId === OrganizationType.JointNominee
        ? 'joint-nominee'
        : 'company-information',
    );
  }

  @action
  setInitialStepId(initialStepId: NominationStepId): void {
    this.initialStepId = initialStepId;
  }

  @action
  markStepsValidated(stepIds: NominationStepId[]): void {
    this.validatedSteps = stepIds.reduce<NominationValidatedSteps>(
      (validatedSteps, stepId) => ({
        ...validatedSteps,
        [stepId]: true,
      }),
      this.validatedSteps,
    );
  }

  @action
  resetStepValidation(stepId: NominationStepId): void {
    this.validatedSteps = {
      ...this.validatedSteps,
      [stepId]: false,
    };
  }

  @action
  expandContact(contactKey: string): void {
    this.expandedContactKeys = arrayWithNewItem(this.expandedContactKeys, contactKey);
  }

  @action
  collapseContact(contactKey: string): void {
    this.expandedContactKeys = arrayWithoutExistingItem(this.expandedContactKeys, contactKey);
  }

  @action
  addContact(): void {
    this.nomination.contacts.push(blankContact());
    const contactKey = crypto.randomUUID();
    this.contactRowKeys.push(contactKey);
    this.expandContact(contactKey);
    this.resetStepValidation('additional-contact');
  }

  @action
  removeContact(contactIndex: number): void {
    const contact = this.nomination.contacts[contactIndex];
    const contactKey = this.contactRowKeys[contactIndex];
    if (contact.contactId > 0) this.deletedContactIds.push(contact.contactId);

    this.nomination.contacts.splice(contactIndex, 1);
    this.contactRowKeys.splice(contactIndex, 1);
    this.expandedContactKeys = arrayWithoutExistingItem(this.expandedContactKeys, contactKey);
    if (this.nomination.contacts.length === 1) {
      this.expandedContactKeys = [this.contactRowKeys[0]];
    }
  }

  @action
  expandJointNominee(jointNomineeKey: string): void {
    this.expandedJointNomineeKeys = arrayWithNewItem(
      this.expandedJointNomineeKeys,
      jointNomineeKey,
    );
  }

  @action
  collapseJointNominee(jointNomineeKey: string): void {
    this.expandedJointNomineeKeys = arrayWithoutExistingItem(
      this.expandedJointNomineeKeys,
      jointNomineeKey,
    );
  }

  @action
  addJointNominee(): void {
    this.nomination.jointNominees.push(createOrganizationDraft(OrganizationType.JointNominee));
    const jointNomineeKey = crypto.randomUUID();
    this.jointNomineeRowKeys.push(jointNomineeKey);
    this.jointNomineeCeoEmailIncludedMap.set(jointNomineeKey, false);
    this.expandJointNominee(jointNomineeKey);
    this.resetStepValidation('joint-nominee');
  }

  @action
  removeJointNominee(jointNomineeIndex: number): void {
    const jointNominee = this.nomination.jointNominees[jointNomineeIndex];
    const jointNomineeKey = this.jointNomineeRowKeys[jointNomineeIndex];
    if (jointNominee.organizationId > 0) this.deletedJointNomineeIds.push(jointNominee.organizationId);

    this.jointNomineeCeoEmailIncludedMap.delete(jointNomineeKey);
    this.nomination.jointNominees.splice(jointNomineeIndex, 1);
    this.jointNomineeRowKeys.splice(jointNomineeIndex, 1);
    this.expandedJointNomineeKeys = arrayWithoutExistingItem(
      this.expandedJointNomineeKeys,
      jointNomineeKey,
    );
  }

  @action
  expandCustomerReference(customerReferenceKey: string): void {
    this.expandedCustomerReferenceKeys = arrayWithNewItem(
      this.expandedCustomerReferenceKeys,
      customerReferenceKey,
    );
  }

  @action
  collapseCustomerReference(customerReferenceKey: string): void {
    this.expandedCustomerReferenceKeys = arrayWithoutExistingItem(
      this.expandedCustomerReferenceKeys,
      customerReferenceKey,
    );
  }

  @action
  addCustomerReference(): void {
    this.nomination.customerReferences.push(blankCustomerReference());
    const customerReferenceKey = crypto.randomUUID();
    this.customerReferenceRowKeys.push(customerReferenceKey);
    this.expandCustomerReference(customerReferenceKey);
    this.resetStepValidation('nomination-submission');
  }

  @action
  removeCustomerReference(customerReferenceIndex: number): void {
    const customerReference = this.nomination.customerReferences[customerReferenceIndex];
    const customerReferenceKey = this.customerReferenceRowKeys[customerReferenceIndex];
    if (customerReference.customerReferenceId > 0) {
      this.deletedCustomerReferenceIds.push(customerReference.customerReferenceId);
    }

    this.nomination.customerReferences.splice(customerReferenceIndex, 1);
    this.customerReferenceRowKeys.splice(customerReferenceIndex, 1);
    this.expandedCustomerReferenceKeys = arrayWithoutExistingItem(
      this.expandedCustomerReferenceKeys,
      customerReferenceKey,
    );
  }

  @action
  addNominationWebsite(): void {
    this.nomination.websites.push(blankNominationWebsite());
    this.nominationWebsiteRowKeys.push(crypto.randomUUID());
    this.resetStepValidation('nomination-submission');
  }

  @action
  removeNominationWebsite(websiteIndex: number): void {
    const website = this.nomination.websites[websiteIndex];
    if (website.websiteId > 0) this.deletedNominationWebsitesIds.push(website.websiteId);

    this.nomination.websites.splice(websiteIndex, 1);
    this.nominationWebsiteRowKeys.splice(websiteIndex, 1);
  }

  @action
  setConfirmPartnerIdModalOpen(isOpen: boolean): void {
    this.isConfirmPartnerIdModalOpen = isOpen;
  }

  @action
  setLoadingType(loadingType?: NominationWizardLoadingType): void {
    this.loadingType = loadingType;
  }

  @action
  setOtherPartnerTypeDescription(value: string, otherPartnerTypeId?: number): void {
    if (otherPartnerTypeId === undefined) return;
    const target = this.nomination.partnerTypes.find((type) => type.partnerTypeId === otherPartnerTypeId);
    if (target) {
      target.description = value;
    }
  }

  @computed
  get associatedCountryIds(): number[] {
    return this.editingAssociatedCountries.map((country) => country.countryId);
  }

  @action
  createAssociatedCountryRequestBodyForUpdate(countries: Country[], naCountry?: Country): void {
    if (!this.isCountryAward) {
      this.editingAssociatedCountries = naCountry ? [naCountry] : [];
      return;
    }

    if (countries.length === 0) {
      this.editingAssociatedCountries = [];
      return;
    }

    this.editingAssociatedCountries = clone(countries);
  }

  setFurthestVisitedStepId(furthestVisitedStepId: NominationStepId): void {
    this.furthestVisitedStepId = furthestVisitedStepId;
  }

  @computed
  get isCountryAward(): boolean {
    return this.selectedAward?.isCountryAward ?? false;
  }

  @action
  setSelectedAward(award: Award): void {
    this.selectedAward = award;
  }

  @action
  setSelectedCountryCode(id: PhoneNumberFieldId, countryCode: CountryCode): void {
    if (this.countryCodeMapById.has(id)) this.countryCodeMapById.delete(id);
    this.countryCodeMapById.set(id, countryCode);
  }

  @action
  createNomination(): void {
    const initialValue: Nomination = blankNomination();

    this.nomination = initialValue;
    this.resetState();
  }

  @action
  createNominationForUpdate(existingNomination: Nomination): void {
    const initialValue = clone(existingNomination);

    if (!initialValue.organization) {
      initialValue.organization = createOrganizationDraft(OrganizationType.Main);
    }

    if (!initialValue.organization.regionalCeoFullName) {
      initialValue.organization.regionalCeoFullName = undefined;
    }

    if (!initialValue.organization.regionalCeoEmail) {
      initialValue.organization.regionalCeoEmail = undefined;
    }

    if (!initialValue.organization.country) {
      initialValue.organization.country = blankCountry();
    }

    initialValue.jointNominees.forEach((jointNominee) => {
      if (!jointNominee.regionalCeoFullName) {
        jointNominee.regionalCeoFullName = undefined;
      }
      
      if (!jointNominee.regionalCeoEmail) {
        jointNominee.regionalCeoEmail = undefined;
      }

      if (!jointNominee.country) {
        jointNominee.country = blankCountry();
      }
    });

    if (initialValue.contacts.length === 0) initialValue.contacts.push(blankContact());

    this.nomination = initialValue;
    this.resetState();
    this.isCeoEmailIncluded = Boolean(this.nomination.organization?.regionalCeoEmail);
    this.nomination.jointNominees.forEach((jointNominee, jointNomineeIndex) => {
      this.jointNomineeCeoEmailIncludedMap.set(
        this.jointNomineeRowKeys[jointNomineeIndex],
        Boolean(jointNominee.regionalCeoEmail),
      );
    });
  }

  private resetState(): void {
    this.validatedSteps = {};
    this.executiveSummaryDocument = undefined;
    this.writtenNominationContent = undefined;
    this.deletedContactIds = [];
    this.deletedJointNomineeIds = [];
    this.deletedCustomerReferenceIds = [];
    this.deletedNominationWebsitesIds = [];
    this.furthestVisitedStepId = 'your-information';
    this.countryCodeMapById.clear();
    this.editingAssociatedCountries = [];
    this.selectedAward = undefined;
    this.initialStepId = 'your-information';
    this.contactRowKeys = this.nomination.contacts.map(() => crypto.randomUUID());
    this.jointNomineeRowKeys = this.nomination.jointNominees.map(() => crypto.randomUUID());
    this.isCeoEmailIncluded = false;
    this.jointNomineeCeoEmailIncludedMap.clear();
    this.customerReferenceRowKeys = this.nomination.customerReferences.map(() => crypto.randomUUID());
    this.nominationWebsiteRowKeys = this.nomination.websites.map(() => crypto.randomUUID());
    this.expandedContactKeys = this.contactRowKeys.length === 1
      ? [this.contactRowKeys[0]]
      : [];
    this.expandedJointNomineeKeys = [];
    this.expandedCustomerReferenceKeys = [];
    this.isConfirmPartnerIdModalOpen = false;
    this.loadingType = 'load';
  }

  @action
  setWrittenNomination(content?: string): void {
    this.writtenNominationContent = content;
  }

  @action
  setMarketplaceOfferUrl(marketplaceOfferUrl?: string): void {
    this.nomination.marketplaceOfferUrl = marketplaceOfferUrl === 'https://' ? undefined : marketplaceOfferUrl;
  }

  @action
  setExecutiveSummaryDocument(documentMetadata?: DocumentMetadata): void {
    this.executiveSummaryDocument = documentMetadata;
  }

  @action
  setNominationOwnerFromUserInfo(userInfo: UserInfo): void {
    const { owner } = this.nomination;
    owner.firstName = userInfo.firstName;
    owner.lastName = userInfo.lastName;
    owner.emailAddress = userInfo.email;
  }

  @action
  setOwnerBodyRequest<K extends keyof Owner>(key: K, value: Owner[K]): void {
    this.nomination.owner[key] = value;
  }

  @action
  setNominationOrganizationUserInfo(userInfo: UserInfo): void {
    const organization = this.nominationOrganization;
    this.nomination.organization = organization;
    organization.firstName = userInfo.firstName;
    organization.lastName = userInfo.lastName;
    organization.emailAddress = userInfo.email;
  }

  @action
  setCeoEmailIncluded(isIncluded: boolean): void {
    this.isCeoEmailIncluded = isIncluded;
    if (!isIncluded && this.nomination.organization) {
      this.nomination.organization.regionalCeoEmail = undefined;
    }
  }

  @action
  setJointNomineeCeoEmailIncluded(jointNomineeRowKey: string, isIncluded: boolean): void {
    const jointNomineeIndex = this.jointNomineeRowKeys.indexOf(jointNomineeRowKey);
    const jointNominee = this.nomination.jointNominees[jointNomineeIndex];
    if (!jointNominee) return;

    this.jointNomineeCeoEmailIncludedMap.set(jointNomineeRowKey, isIncluded);
    if (!isIncluded) jointNominee.regionalCeoEmail = undefined;
  }

  @action
  setNominationAward(): void {
    if (!this.selectedAward) return;
    const { award } = this.nomination;
    award.awardId = this.selectedAward.awardId;
    award.allowJointNomination = this.selectedAward.allowJointNomination;
    award.title = this.selectedAward.title;
    award.detail = this.selectedAward.detail;
    award.summary = this.selectedAward.summary;
    award.requireWrittenNomination = this.selectedAward.requireWrittenNomination;
    award.requireCustomerReference = this.selectedAward.requireCustomerReference;
  }

  getValidationInput(otherPartnerTypeId?: number): NominationWizardValidationInput {
    return {
      nomination: this.nomination,
      organization: this.nominationOrganization,
      editingAssociatedCountries: this.editingAssociatedCountries,
      otherPartnerTypeId,
      isCountryAward: this.isCountryAward,
      organizationPhoneCountryCode: this.countryCodeMapById.get(PhoneNumberField.Organization),
      requiresWrittenNomination: this.nomination.award.requireWrittenNomination,
      writtenNominationContent: this.writtenNominationContent ?? '',
      hasExecutiveSummaryDocument: this.hasExecutiveSummaryDocument,
      isCeoEmailIncluded: this.isCeoEmailIncluded,
      jointNomineeCeoEmailIncluded: this.jointNomineeRowKeys.map(
        (jointNomineeRowKey) => this.jointNomineeCeoEmailIncludedMap.get(jointNomineeRowKey) || false,
      ),
    };
  }

  @computed
  get yourInformationValidation(): ValidationState<YourInformationValidation> {
    return createValidationState(yourInformationValidationSchema.safeParse(this.getValidationInput()));
  }

  getCompanyInformationValidation(otherPartnerTypeId?: number): ValidationState<NominationWizardCompanyInformationValidation> {
    return createValidationState(
      nominationWizardCompanyInformationValidationSchema.safeParse(this.getValidationInput(otherPartnerTypeId)),
    );
  }

  @computed
  get additionalContactValidations(): ValidationState<AdditionalContactValidation>[] {
    return this.nomination.contacts.map((contact) =>
      createValidationState(additionalContactValidationSchema.safeParse(contact)));
  }

  @computed
  get jointNomineeValidations(): ValidationState<NominationWizardJointNomineeValidation>[] {
    return this.nomination.jointNominees.map((jointNominee, jointNomineeIndex) => createValidationState(
      nominationWizardJointNomineeValidationSchema.safeParse({
        jointNominee,
        isCeoEmailIncluded:
          this.jointNomineeCeoEmailIncludedMap.get(this.jointNomineeRowKeys[jointNomineeIndex]) || false,
      }),
    ));
  }

  @computed
  get nominationSubmissionValidation(): ValidationState<NominationSubmissionValidation> {
    return createValidationState(
      nominationSubmissionValidationSchema.safeParse(this.getValidationInput()),
    );
  }

  @computed
  get executiveSummaryDocumentErrorMessage(): string {
    if (!this.isBeingValidated('nomination-submission')) return '';

    return this.nominationSubmissionValidation.fieldErrors.hasExecutiveSummaryDocument?.join(' ') ?? '';
  }

  @computed
  get nominationWebsiteValidations(): ValidationState<NominationWebsiteValidation>[] {
    return this.nomination.websites.map(website => createValidationState(
      nominationWebsiteValidationSchema.safeParse(website),
    ));
  }

  @computed
  get customerReferenceValidations(): ValidationState<CustomerReferenceValidation>[] {
    return this.nomination.customerReferences.map(customerReference => createValidationState(
      customerReferenceValidationSchema.safeParse(customerReference),
    ));
  }

  isStepValid(stepId: NominationStepId, otherPartnerTypeId?: number): boolean {
    switch (stepId) {
      case 'your-information':
        return this.yourInformationValidation.isValid;
      case 'company-information':
        return this.getCompanyInformationValidation(otherPartnerTypeId).isValid;
      case 'additional-contact':
        return this.additionalContactValidations.every(validation => validation.isValid);
      case 'joint-nominee':
        return this.jointNomineeValidations.every(validation => validation.isValid);
      case 'nomination-submission':
        return this.nominationSubmissionValidation.isValid;
    }
  }

  @action
  setCompanyInfoDescription(description: string): void {
    this.companyInfoDescription = description;
  }

  @action
  setNominationSubmissionDescription(description: string): void {
    this.nominationSubmissionDescription = description;
  }
}
